import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";

import { ApiError } from "../api/errors";
import { useAuth } from "./AuthProvider";
import "./login.css";

export function LoginPage() {
  const { signIn, passwordJustChanged } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // The password screen cannot show its own success: changing a password ends
  // every session, so `RequireAuth` has already redirected it away by the time
  // there is anything to say. The provider carries the one fact across.

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await signIn(email, password);
      // Root, not a named section: which screen this administrator may land on
      // depends on what they may do, and that decision lives in one place
      // rather than being repeated by everything that navigates.
      navigate("/", { replace: true });
    } catch (caught) {
      if (caught instanceof ApiError && caught.kind === "unauthenticated") {
        setError("Incorrect email or password.");
      } else if (caught instanceof ApiError && caught.kind === "network") {
        setError("The server could not be reached. Check that the backend is running.");
      } else if (caught instanceof ApiError) {
        setError(caught.userMessage);
      } else {
        setError("Sign in could not be completed.");
      }
      // The password stays in the field so a typo can be corrected, but it is
      // never persisted anywhere outside this component's state.
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="login">
      <form className="login__card panel" onSubmit={onSubmit}>
        <div className="login__brand">
          <span className="login__mark" aria-hidden="true" />
          <h1 className="login__title">OptiQ Admin</h1>
        </div>
        <p className="login__lead muted">Sign in to manage the product catalog.</p>

        {passwordJustChanged ? (
          <div className="notice" role="status">
            Your password was changed. Sign in with the new one.
          </div>
        ) : null}

        {error ? (
          <div className="notice notice--error" role="alert">
            {error}
          </div>
        ) : null}

        <div>
          <label htmlFor="email">Email</label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={busy}
          />
        </div>

        <div>
          <label htmlFor="password">Password</label>
          <input
            id="password"
            name="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={busy}
          />
        </div>

        <button type="submit" className="btn btn--primary login__submit" disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </main>
  );
}
